import { createElement, FC, ReactHTML, RefObject, useCallback, useRef } from 'react'
import styles from 'components/features/pfm/Button/DispatchButton.styl'
import cn from 'classnames'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import Button, { ButtonProps } from 'components/features/pfm/Button/Button'
import { Action } from 'actions'

export type BaseDispatchButtonProps =
  & ButtonProps
  & {
    renderAsLink?: boolean
    component?: keyof ReactHTML // Render as a click handler on any element instead of a button
    beforeOnClick?: () => void // Any additional side-effects can be triggered with this
    afterOnClick?: () => void // Any additional side-effects can be t/credit-cardriggered with this
  }

type DispatchButtonProps =
  & BaseDispatchButtonProps
  & {
    actions: Action[]
  }

export const DispatchButton: FC<DispatchButtonProps> = (props) => {
  const {
    actions,
    beforeOnClick,
    component,
    afterOnClick,
    disabled,
    innerRef,
    renderAsLink,
    fullWidth = true,
    ...buttonProps
  } = props

  const dispatch = useStoreDispatch()
  const newRef = useRef<HTMLElement>(null)
  const ref = innerRef || newRef

  const handleClick = useCallback(() => {
    !!beforeOnClick && beforeOnClick()
    !disabled && actions.forEach(dispatch)
    !!afterOnClick && afterOnClick()
  }, [beforeOnClick, disabled, actions, dispatch, afterOnClick])

  if (!!component) {
    const componentClasses = cn(
      props.className,
      styles.component, {
        [styles.fullWidth]: fullWidth,
        [styles.isDisabled]: disabled || buttonProps.isSubmitting,
      }
    )
    const newProps = {
      onClick: handleClick,
      tabIndex: 0,
      role: 'button',
      ref: ref as RefObject<HTMLDivElement>,
      className: componentClasses,
    }

    return createElement(component, newProps, props.children)
  }

  return !renderAsLink ? (
    <Button { ...buttonProps } onClick={ handleClick } disabled={ disabled } innerRef={ ref as RefObject<HTMLButtonElement> } fullWidth={ fullWidth } />
  ) : (
    <a onClick={ handleClick } className={ cn(props.className, { [styles.isDisabled]: disabled }) } ref={ ref as RefObject<HTMLAnchorElement> }>
      { props.text || props.children }
    </a>
  )
}
